import { ResolverState } from '../react';

// Default window within which peer-provided data is treated as fresh. Beyond
// this, an adopted value is applied on the stale side (shown immediately while
// a refresh runs) so bounced data can't stay locked in indefinitely.
// 10m — 2x the GC MAX_PERIOD (5m) ceiling on how long a detached resource
// survives in a peer's memory, so the TTL catches continuously-bounced data
// rather than racing collection.
export const PEER_SYNC_DEFAULT_MAX_AGE = 1000 * 60 * 10;

export type PeerValue = { data: unknown; storedAt?: number; stale: boolean };

let __enabled = true;

export const __testOnlyDisablePeerSync = () => {
  __enabled = false;
};

export const __testOnlyEnablePeerSync = () => {
  __enabled = true;
};

export const peerSyncEnabled = (): boolean => {
  return __enabled;
};

const initResponseEvent = (instanceKey: string) =>
  `pulse:peer-response:${instanceKey}`;

const dispatchInitResponse = (
  instanceKey: string,
  clientId: string,
  value: PeerValue
) =>
  window.dispatchEvent(
    new CustomEvent(initResponseEvent(instanceKey), {
      detail: { ...value, sourceId: clientId },
    })
  );

const INIT_REQUEST_EVENT = 'pulse:peer-request';

type InitRequestEvent = CustomEvent<{
  instanceKey: string;
  sourceId: string;
}>;

const dispatchInitRequest = (instanceKey: string, clientId: string) =>
  window.dispatchEvent(
    new CustomEvent(INIT_REQUEST_EVENT, {
      detail: { instanceKey, sourceId: clientId },
    })
  );

type ResponseEvent = CustomEvent<PeerValue & { sourceId: string }>;

type StateBatchEntry = { instanceKey: string } & PeerValue;

const STATE_BATCH_EVENT = 'pulse:state-batch';

type StateBatchEvent = CustomEvent<{
  changes: StateBatchEntry[];
  sourceId: string;
}>;

export const peerSyncManager = ({
  clientId,
  getState,
  getStoredAt,
  applyBatch,
}: {
  clientId: string;
  getState: (instanceKey: string) => ResolverState<any, any> | undefined;
  getStoredAt: (instanceKey: string) => number | undefined;
  applyBatch: (changes: StateBatchEntry[]) => void;
}) => {
  const onInitRequest = (event: InitRequestEvent) => {
    if (peerSyncEnabled() && event.detail.sourceId !== clientId) {
      const state = getState(event.detail.instanceKey);

      if (state?.data !== undefined && !state.loading && !state.error) {
        dispatchInitResponse(event.detail.instanceKey, clientId, {
          data: state.data,
          storedAt: getStoredAt(event.detail.instanceKey),
          stale: state.stale,
        });
      }
    }
  };

  window.addEventListener(INIT_REQUEST_EVENT, onInitRequest as any);

  const onStateBatch = ({ detail: { changes, sourceId } }: StateBatchEvent) => {
    if (sourceId !== clientId) {
      applyBatch(changes);
    }
  };

  window.addEventListener(STATE_BATCH_EVENT, onStateBatch as any);

  let batch: StateBatchEntry[] = [];

  return {
    pull: (instanceKey: string): PeerValue | undefined => {
      if (!peerSyncEnabled()) {
        return undefined;
      }

      // Responses fire synchronously during dispatch, so multiple peers may
      // answer. Keep the freshest by storedAt so we don't adopt an older copy.
      let result: PeerValue | undefined;

      const listener = ({ detail }: ResponseEvent) => {
        if (detail.sourceId !== clientId && detail.data !== undefined) {
          if (!result || (detail.storedAt ?? 0) > (result.storedAt ?? 0)) {
            result = detail;
          }
        }
      };

      const responseEventName = initResponseEvent(instanceKey);
      window.addEventListener(responseEventName, listener as any);
      dispatchInitRequest(instanceKey, clientId);
      window.removeEventListener(responseEventName, listener as any);

      return result;
    },
    collect: (
      instanceKey: string,
      data: unknown,
      storedAt: number | undefined,
      stale: boolean
    ) => {
      if (peerSyncEnabled()) {
        batch.push({ instanceKey, data, storedAt, stale });
      }
    },
    flush: () => {
      if (batch.length === 0) {
        return;
      }

      const changes = batch;
      batch = [];

      window.dispatchEvent(
        new CustomEvent(STATE_BATCH_EVENT, {
          detail: { changes, sourceId: clientId },
        })
      );
    },
    destroy: () => {
      window.removeEventListener(INIT_REQUEST_EVENT, onInitRequest as any);
      window.removeEventListener(STATE_BATCH_EVENT, onStateBatch as any);
      batch = [];
    },
  };
};
